import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Code2, Loader2, ImagePlus, Link2, List, ListOrdered, Pilcrow, Quote, Redo2, Type, Undo2 } from "lucide-react";
import { sanitizeRichTextHtml } from "@/lib/richText";

interface RichTextEditorProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onImageUpload?: (file: File) => Promise<string>;
  uploadingImage?: boolean;
}

export function RichTextEditor({
  label,
  value,
  onChange,
  placeholder,
  onImageUpload,
  uploadingImage = false,
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [focused, setFocused] = useState(false);
  const [sourceMode, setSourceMode] = useState(false);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const sanitized = sanitizeRichTextHtml(value || "");
    if (editor.innerHTML !== sanitized) {
      editor.innerHTML = sanitized;
    }
  }, [value]);

  const emitChange = () => {
    const html = sanitizeRichTextHtml(editorRef.current?.innerHTML || "");
    onChange(html);
  };

  const applyCommand = (command: string, commandValue?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, commandValue);
    emitChange();
  };

  const handleLink = () => {
    const href = window.prompt("Enter the link URL");
    if (!href) return;
    applyCommand("createLink", href.trim());
  };

  const handleImageFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !onImageUpload) return;

    const url = await onImageUpload(file);
    if (!url) return;

    editorRef.current?.focus();
    document.execCommand("insertHTML", false, `<p><img src="${url}" alt="" /></p>`);
    emitChange();
  };

  const handleSourceModeToggle = () => {
    if (sourceMode) {
      onChange(sanitizeRichTextHtml(value || ""));
    }
    setSourceMode((current) => !current);
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="rounded-2xl border border-border bg-card shadow-sm">
        <div className="flex flex-wrap gap-2 border-b border-border p-3">
          <Button
            type="button"
            size="sm"
            variant={sourceMode ? "default" : "outline"}
            onClick={handleSourceModeToggle}
          >
            <Code2 className="mr-2 h-4 w-4" />
            HTML
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => applyCommand("bold")}>
            <Type className="mr-2 h-4 w-4" />
            Bold
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => applyCommand("italic")}>
            Italic
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => applyCommand("formatBlock", "h2")}>
            H2
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => applyCommand("formatBlock", "h3")}>
            H3
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => applyCommand("formatBlock", "p")}>
            <Pilcrow className="mr-2 h-4 w-4" />
            Paragraph
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => applyCommand("insertUnorderedList")}>
            <List className="mr-2 h-4 w-4" />
            Bullets
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => applyCommand("insertOrderedList")}>
            <ListOrdered className="mr-2 h-4 w-4" />
            Numbers
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => applyCommand("formatBlock", "blockquote")}>
            <Quote className="mr-2 h-4 w-4" />
            Quote
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={handleLink}>
            <Link2 className="mr-2 h-4 w-4" />
            Link
          </Button>
          {onImageUpload && (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImage}
              >
                {uploadingImage ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ImagePlus className="mr-2 h-4 w-4" />
                )}
                Image
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageFile}
                className="hidden"
              />
            </>
          )}
          <Button type="button" size="sm" variant="ghost" onClick={() => applyCommand("undo")}>
            <Undo2 className="h-4 w-4" />
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => applyCommand("redo")}>
            <Redo2 className="h-4 w-4" />
          </Button>
        </div>

        {sourceMode ? (
          <Textarea
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onBlur={() => onChange(sanitizeRichTextHtml(value || ""))}
            className="min-h-[320px] rounded-none border-0 font-mono text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
            placeholder="<p>Paste blog HTML here...</p>"
          />
        ) : (
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onInput={emitChange}
            onBlur={emitChange}
            onFocus={() => setFocused(true)}
            className={`min-h-[320px] overflow-x-auto px-4 py-3 outline-none [&_blockquote]:border-l-4 [&_blockquote]:border-primary/30 [&_blockquote]:pl-4 [&_caption]:mb-3 [&_caption]:text-sm [&_caption]:text-muted-foreground [&_img]:my-4 [&_img]:h-auto [&_img]:max-w-full [&_img]:rounded-2xl [&_li]:ml-6 [&_ol]:list-decimal [&_p]:mb-4 [&_table]:my-6 [&_table]:w-full [&_table]:min-w-[640px] [&_table]:border-collapse [&_tbody_tr:nth-child(odd)]:bg-muted/30 [&_td]:border [&_td]:border-border [&_td]:p-3 [&_td]:align-top [&_th]:border [&_th]:border-border [&_th]:bg-muted [&_th]:p-3 [&_th]:text-left [&_th]:font-semibold [&_ul]:list-disc ${
              focused ? "bg-background" : ""
            }`}
            data-placeholder={placeholder || "Start writing..."}
          />
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Visual editor supports headings, lists, links, quotes, uploaded images, and HTML tables.
      </p>
    </div>
  );
}
